import {
  FacebookAuthProvider,
  GoogleAuthProvider,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
} from 'firebase/auth'
import { useQueryClient } from 'react-query'

import { useMutation } from 'hooks/useMutation'

import { analytics, logEvent } from 'utils/firebase'

import { useAuth as useAuthContext } from 'contexts/AuthContext'

interface EmailPasswordPayload {
  email: string
  password: string
}

export const useAuth = () => {
  const { authUser, firebaseAuthObject, ready } = useAuthContext()
  const queryClient = useQueryClient()

  const logout = useMutation(() => signOut(firebaseAuthObject), {
    onSuccess: () => {
      queryClient.invalidateQueries()
    },
  })

  const loginGoogle = useMutation(async () => {
    const provider = new GoogleAuthProvider()
    provider.addScope('')

    const result = await signInWithPopup(firebaseAuthObject, provider)
    const credential = GoogleAuthProvider.credentialFromResult(result)

    logEvent(analytics, 'login', { method: 'google' })

    return { credential, result }
  })

  const loginFacebook = useMutation(async () => {
    const provider = new FacebookAuthProvider()
    provider.addScope('')

    const result = await signInWithPopup(firebaseAuthObject, provider)
    const credential = FacebookAuthProvider.credentialFromResult(result)

    logEvent(analytics, 'login', { method: 'facebook' })

    return { credential, result }
  })

  const loginUsernamePassword = useMutation(async ({ email, password }: EmailPasswordPayload) => {
    await signInWithEmailAndPassword(firebaseAuthObject, email, password)

    logEvent(analytics, 'login', { method: 'email' })
  })

  const signupUsernamePassword = useMutation(async ({ email, password }: EmailPasswordPayload) => {
    await createUserWithEmailAndPassword(firebaseAuthObject, email, password)

    logEvent(analytics, 'sign_up', { method: 'email' })
  })

  const resetPassword = useMutation(async ({ email }: { email: string }) => {
    await sendPasswordResetEmail(firebaseAuthObject, email)

    logEvent(analytics, 'reset_password', { method: 'email' })
  })

  return {
    ...authUser,
    ready,

    // Mutation Objects
    logout,
    login: {
      google: loginGoogle,
      facebook: loginFacebook,
      apple: () => {},
      usernamePassword: loginUsernamePassword,
    },
    signup: {
      usernamePassword: signupUsernamePassword,
    },
    resetPassword,
  }
}
